import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import CreateList from './CreateList';
import ImportCSV from './ImportCSV';

export default function Dashboard({ user }) {
  const [contacts, setContacts] = useState([]);
  const [addressBooks, setAddressBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('alphabetical');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [showCreateList, setShowCreateList] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [selectedContactsForList, setSelectedContactsForList] = useState([]);
  const [showImportCSV, setShowImportCSV] = useState(false);
  
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateContacts, setDuplicateContacts] = useState([]);
  const [pendingContact, setPendingContact] = useState(null);
  
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    birthMonth: '',
    birthDay: '',
    hasSignificantOther: false,
    soFirstName: '',
    soLastName: '',
    soPhone: '',
    soBirthMonth: '',
    soBirthDay: '',
    notes: '',
    tags: []
  });
  const [saving, setSaving] = useState(false);

  const availableTags = [
    { id: 'vip', label: 'VIP', color: '#ec4899' },
    { id: 'family', label: 'Family', color: '#10b981' },
    { id: 'work', label: 'Work', color: '#3b82f6' },
    { id: 'friend', label: 'Friend', color: '#f59e0b' },
    { id: 'neighbor', label: 'Neighbor', color: '#8b5cf6' }
  ];

  function getMonthNumber(monthName) {
    const months = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return months[monthName] || '01';
  }

  function formatBirthdayForDB(month, day) {
    if (!month || !day) return null;
    const monthNum = getMonthNumber(month);
    const dayNum = String(day).padStart(2, '0');
    return `2024-${monthNum}-${dayNum}`;
  }

  function formatBirthdayForDisplay(dbDate) {
    if (!dbDate) return null;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const date = new Date(dbDate + 'T00:00:00');
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}`;
  }

  async function checkForDuplicates(firstName, lastName) {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('address_book_id', selectedBook.id)
        .ilike('first_name', firstName.trim())
        .ilike('last_name', lastName.trim());

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return [];
    }
  }

  function calculateUpcomingBirthdays(contactsList) {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
    
    const upcoming = contactsList.filter(contact => {
      if (!contact.birthday) return false;
      
      const birthday = new Date(contact.birthday + 'T00:00:00');
      const thisYearBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
      
      if (thisYearBirthday < today) {
        thisYearBirthday.setFullYear(today.getFullYear() + 1);
      }
      
      return thisYearBirthday >= today && thisYearBirthday <= thirtyDaysFromNow;
    }).sort((a, b) => {
      const aDate = new Date(a.birthday + 'T00:00:00');
      const bDate = new Date(b.birthday + 'T00:00:00');
      const aThisYear = new Date(today.getFullYear(), aDate.getMonth(), aDate.getDate());
      const bThisYear = new Date(today.getFullYear(), bDate.getMonth(), bDate.getDate());
      return aThisYear - bThisYear;
    });
    
    return upcoming.slice(0, 5);
  }

  useEffect(() => {
    loadAddressBooks();
  }, [user.id]);

  useEffect(() => {
    if (selectedBook) {
      loadLists();
    }
  }, [selectedBook]);

  useEffect(() => {
    setUpcomingBirthdays(calculateUpcomingBirthdays(contacts));
  }, [contacts]);

  async function loadLists() {
    try {
      const { data, error } = await supabase
        .from('contact_lists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLists(data || []);
    } catch (error) {
      console.error('Error loading lists:', error);
    }
  }

  async function loadAddressBooks() {
    try {
      const { data: collabData, error: collabError } = await supabase
        .from('collaborators')
        .select('address_book_id')
        .eq('user_id', user.id);

      if (collabError) throw collabError;

      const collabBookIds = collabData.map(c => c.address_book_id);
      
      let sharedBooks = [];
      if (collabBookIds.length > 0) {
        const { data: sharedData, error: sharedError } = await supabase
          .from('address_books')
          .select('*')
          .in('id', collabBookIds);

        if (sharedError) throw sharedError;
        sharedBooks = sharedData || [];
      }

      const { data: ownedBooks, error: ownedError } = await supabase
        .from('address_books')
        .select('*')
        .eq('owner_id', user.id);

      if (ownedError) throw ownedError;

      const allBooks = [...sharedBooks, ...(ownedBooks || [])];
      setAddressBooks(allBooks);

      if (allBooks.length > 0) {
        setSelectedBook(allBooks[0]);
        loadContacts(allBooks[0].id);
        loadCollaborators(allBooks[0].id);
      } else {
        await createDefaultBook();
      }
    } catch (error) {
      console.error('Error loading address books:', error);
      setLoading(false);
    }
  }

  async function createDefaultBook() {
    try {
      const { data, error } = await supabase
        .from('address_books')
        .insert([{
          owner_id: user.id,
          title: 'My Contacts',
          slug: `${user.id.substring(0, 8)}-contacts`,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;

      setAddressBooks([data]);
      setSelectedBook(data);
      loadContacts(data.id);
      loadCollaborators(data.id);
    } catch (error) {
      console.error('Error creating default book:', error);
      setLoading(false);
    }
  }

  async function loadCollaborators(bookId) {
    try {
      const { data, error } = await supabase
        .from('collaborators')
        .select('id, user_id, role')
        .eq('address_book_id', bookId);

      if (error) throw error;

      const enrichedData = await Promise.all(
        (data || []).map(async (collab) => {
          const { data: userData } = await supabase
            .from('users')
            .select('email')
            .eq('id', collab.user_id)
            .single();
          
          return {
            ...collab,
            users: userData
          };
        })
      );

      setCollaborators(enrichedData);
    } catch (error) {
      console.error('Error loading collaborators:', error);
    }
  }

  async function loadContacts(bookId) {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('address_book_id', bookId)
        .order('submitted_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setContacts(data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadContactsForList(listId) {
    try {
      setLoading(true);
      
      const { data: members, error: membersError } = await supabase
        .from('contact_list_members')
        .select('contact_id')
        .eq('list_id', listId);

      if (membersError) throw membersError;

      if (members.length === 0) {
        setContacts([]);
        setLoading(false);
        return;
      }

      const contactIds = members.map(m => m.contact_id);

      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .in('id', contactIds)
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      
      setContacts(data || []);
    } catch (error) {
      console.error('Error loading contacts for list:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectList(list) {
    setSelectedList(list);
    setSelectedContactsForList([]);
    setBulkMode(false);
    setSelectedContactIds([]);
    if (list === null) {
      loadContacts(selectedBook.id);
    } else {
      loadContactsForList(list.id);
    }
  }

  async function addContactsToList(listId) {
    if (selectedContactsForList.length === 0) {
      alert('Please select at least one contact');
      return;
    }

    try {
      const inserts = selectedContactsForList.map(contactId => ({
        contact_id: contactId,
        list_id: listId
      }));

      const { error } = await supabase
        .from('contact_list_members')
        .insert(inserts);

      if (error) throw error;

      alert('Contacts added to list!');
      setShowAddToList(false);
      setSelectedContactsForList([]);
    } catch (error) {
      console.error('Error adding contacts to list:', error);
      alert('Error: ' + error.message);
    }
  }

  async function removeContactFromList(contactId, contactName) {
    if (!selectedList) return;
    
    if (!window.confirm(`Remove ${contactName} from "${selectedList.name}"? They will remain in your All Contacts.`)) return;

    try {
      const { error } = await supabase
        .from('contact_list_members')
        .delete()
        .eq('list_id', selectedList.id)
        .eq('contact_id', contactId);

      if (error) throw error;

      loadContactsForList(selectedList.id);
    } catch (error) {
      console.error('Error removing contact from list:', error);
      alert('Error: ' + error.message);
    }
  }

  function toggleContactSelection(contactId) {
    if (selectedContactsForList.includes(contactId)) {
      setSelectedContactsForList(selectedContactsForList.filter(id => id !== contactId));
    } else {
      setSelectedContactsForList([...selectedContactsForList, contactId]);
    }
  }

  function toggleBulkSelection(contactId) {
    if (selectedContactIds.includes(contactId)) {
      setSelectedContactIds(selectedContactIds.filter(id => id !== contactId));
    } else {
      setSelectedContactIds([...selectedContactIds, contactId]);
    }
  }

  function selectAllContacts() {
    const allIds = displayContacts.map(c => c.id);
    setSelectedContactIds(allIds);
  }

  function deselectAllContacts() {
    setSelectedContactIds([]);
  }

  async function bulkDeleteContacts() {
    if (selectedContactIds.length === 0) {
      alert('No contacts selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedContactIds.length} contacts? This cannot be undone.`)) return;

    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .in('id', selectedContactIds);

      if (error) throw error;

      alert(`${selectedContactIds.length} contacts deleted!`);
      setSelectedContactIds([]);
      setBulkMode(false);
      
      if (selectedList) {
        loadContactsForList(selectedList.id);
      } else {
        loadContacts(selectedBook.id);
      }
    } catch (error) {
      console.error('Error deleting contacts:', error);
      alert('Error: ' + error.message);
    }
  }

  async function bulkAddToList(listId) {
    if (selectedContactIds.length === 0) {
      alert('No contacts selected');
      return;
    }

    try {
      const inserts = selectedContactIds.map(contactId => ({
        contact_id: contactId,
        list_id: listId
      }));

      const { error } = await supabase
        .from('contact_list_members')
        .insert(inserts);

      if (error) throw error;

      alert(`${selectedContactIds.length} contacts added to list!`);
      setSelectedContactIds([]);
      setBulkMode(false);
    } catch (error) {
      console.error('Error adding contacts to list:', error);
      alert('Error: ' + error.message);
    }
  }

  async function addContact() {
    if (!newContact.firstName || !newContact.lastName) {
      alert('Please enter at least first and last name');
      return;
    }

    const duplicates = await checkForDuplicates(newContact.firstName, newContact.lastName);
    
    if (duplicates.length > 0) {
      setDuplicateContacts(duplicates);
      setPendingContact(newContact);
      setShowDuplicateWarning(true);
      return;
    }

    await saveNewContact(newContact);
  }

  async function saveNewContact(contactData) {
    setSaving(true);

    try {
      const birthday = formatBirthdayForDB(contactData.birthMonth, contactData.birthDay);
      const soBirthday = contactData.hasSignificantOther 
        ? formatBirthdayForDB(contactData.soBirthMonth, contactData.soBirthDay)
        : null;

      const significantOther = contactData.hasSignificantOther && (contactData.soFirstName || contactData.soLastName)
        ? `${contactData.soFirstName} ${contactData.soLastName}`.trim()
        : null;

      const { error } = await supabase
        .from('contacts')
        .insert({
          address_book_id: selectedBook.id,
          first_name: contactData.firstName,
          last_name: contactData.lastName,
          email: contactData.email || null,
          phone: contactData.phone || null,
          street: contactData.street || null,
          city: contactData.city || null,
          state: contactData.state || null,
          zip_code: contactData.zip || null,
          birthday: birthday,
          so_name: significantOther,
          so_phone: contactData.hasSignificantOther ? (contactData.soPhone || null) : null,
          so_birthday: soBirthday,
          notes: contactData.notes || null,
          tags: contactData.tags || [],
          submitted_at: new Date().toISOString()
        });

      if (error) throw error;

      setShowAddContact(false);
      setNewContact({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        birthMonth: '',
        birthDay: '',
        hasSignificantOther: false,
        soFirstName: '',
        soLastName: '',
        soPhone: '',
        soBirthMonth: '',
        soBirthDay: '',
        notes: '',
        tags: []
      });
      
      if (selectedList) {
        loadContactsForList(selectedList.id);
      } else {
        loadContacts(selectedBook.id);
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Error adding contact: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  function handleAddAnyway() {
    setShowDuplicateWarning(false);
    saveNewContact(pendingContact);
  }

  function handleMergeDuplicate(existingContact) {
    const merged = {
      firstName: pendingContact.firstName || existingContact.first_name,
      lastName: pendingContact.lastName || existingContact.last_name,
      email: pendingContact.email || existingContact.email,
      phone: pendingContact.phone || existingContact.phone,
      street: pendingContact.street || existingContact.street,
      city: pendingContact.city || existingContact.city,
      state: pendingContact.state || existingContact.state,
      zip: pendingContact.zip || existingContact.zip_code,
      birthMonth: pendingContact.birthMonth || (existingContact.birthday ? formatBirthdayForDisplay(existingContact.birthday)?.split(' ')[0] : ''),
      birthDay: pendingContact.birthDay || (existingContact.birthday ? formatBirthdayForDisplay(existingContact.birthday)?.split(' ')[1] : ''),
      hasSignificantOther: pendingContact.hasSignificantOther || !!existingContact.so_name,
      soFirstName: pendingContact.soFirstName || existingContact.so_name?.split(' ')[0] || '',
      soLastName: pendingContact.soLastName || existingContact.so_name?.split(' ').slice(1).join(' ') || '',
      soPhone: pendingContact.soPhone || existingContact.so_phone,
      soBirthMonth: pendingContact.soBirthMonth || (existingContact.so_birthday ? formatBirthdayForDisplay(existingContact.so_birthday)?.split(' ')[0] : ''),
      soBirthDay: pendingContact.soBirthDay || (existingContact.so_birthday ? formatBirthdayForDisplay(existingContact.so_birthday)?.split(' ')[1] : ''),
      notes: pendingContact.notes || existingContact.notes || '',
      tags: [...new Set([...(pendingContact.tags || []), ...(existingContact.tags || [])])]
    };

    setEditingContact({
      id: existingContact.id,
      ...merged
    });
    
    setShowDuplicateWarning(false);
    setShowAddContact(false);
    setShowEditContact(true);
  }

  function openEditContact(contact) {
    let birthMonth = '', birthDay = '';
    if (contact.birthday) {
      const displayBirthday = formatBirthdayForDisplay(contact.birthday);
      if (displayBirthday) {
        [birthMonth, birthDay] = displayBirthday.split(' ');
      }
    }

    let soBirthMonth = '', soBirthDay = '';
    if (contact.so_birthday) {
      const displaySoBirthday = formatBirthdayForDisplay(contact.so_birthday);
      if (displaySoBirthday) {
        [soBirthMonth, soBirthDay] = displaySoBirthday.split(' ');
      }
    }
    
    setEditingContact({
      id: contact.id,
      firstName: contact.first_name || '',
      lastName: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      street: contact.street || '',
      city: contact.city || '',
      state: contact.state || '',
      zip: contact.zip_code || '',
      birthMonth: birthMonth || '',
      birthDay: birthDay || '',
      hasSignificantOther: !!(contact.so_name || contact.significant_other),
      soFirstName: (contact.so_name || contact.significant_other) ? (contact.so_name || contact.significant_other).split(' ')[0] : '',
      soLastName: (contact.so_name || contact.significant_other) ? (contact.so_name || contact.significant_other).split(' ').slice(1).join(' ') : '',
      soPhone: contact.so_phone || '',
      soBirthMonth: soBirthMonth || '',
      soBirthDay: soBirthDay || '',
      notes: contact.notes || '',
      tags: contact.tags || []
    });
    setShowEditContact(true);
  }

  async function saveEditContact() {
    if (!editingContact.firstName || !editingContact.lastName) {
      alert('Please enter at least first and last name');
      return;
    }

    setSaving(true);

    try {
      const birthday = formatBirthdayForDB(editingContact.birthMonth, editingContact.birthDay);
      const soBirthday = editingContact.hasSignificantOther 
        ? formatBirthdayForDB(editingContact.soBirthMonth, editingContact.soBirthDay)
        : null;

      const significantOther = editingContact.hasSignificantOther && (editingContact.soFirstName || editingContact.soLastName)
        ? `${editingContact.soFirstName} ${editingContact.soLastName}`.trim()
        : null;

      const { error } = await supabase
        .from('contacts')
        .update({
          first_name: editingContact.firstName,
          last_name: editingContact.lastName,
          email: editingContact.email || null,
          phone: editingContact.phone || null,
          street: editingContact.street || null,
          city: editingContact.city || null,
          state: editingContact.state || null,
          zip_code: editingContact.zip || null,
          birthday: birthday,
          so_name: significantOther,
          so_phone: editingContact.hasSignificantOther ? (editingContact.soPhone || null) : null,
          so_birthday: soBirthday,
          notes: editingContact.notes || null,
          tags: editingContact.tags || []
        })
        .eq('id', editingContact.id);

      if (error) throw error;

      setShowEditContact(false);
      setEditingContact(null);
      
      if (selectedList) {
        loadContactsForList(selectedList.id);
      } else {
        loadContacts(selectedBook.id);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Error updating contact: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteContact(contactId, contactName) {
    if (!window.confirm(`Delete ${contactName}? This cannot be undone.`)) return;

    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId);

      if (error) throw error;

      setShowEditContact(false);
      setEditingContact(null);
      
      if (selectedList) {
        loadContactsForList(selectedList.id);
      } else {
        loadContacts(selectedBook.id);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error: ' + error.message);
    }
  }

  async function inviteCollaborator() {
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email')
        .ilike('email', inviteEmail.trim())
        .single();

      if (userError || !userData) {
        alert('User not found. They need to create an account first at: ' + window.location.origin);
        return;
      }

      if (userData.id === user.id) {
        alert("You can't invite yourself!");
        return;
      }

      const { data: existing } = await supabase
        .from('collaborators')
        .select('id')
        .eq('address_book_id', selectedBook.id)
        .eq('user_id', userData.id)
        .maybeSingle();

      if (existing) {
        alert('This user is already a collaborator!');
        return;
      }

      const { error: insertError } = await supabase
        .from('collaborators')
        .insert({
          address_book_id: selectedBook.id,
          user_id: userData.id,
          role: 'editor',
          invited_by: user.id
        });

      if (insertError) throw insertError;

      alert('Collaborator added successfully!');
      setInviteEmail('');
      setShowInvite(false);
      loadCollaborators(selectedBook.id);
    } catch (error) {
      console.error('Error inviting collaborator:', error);
      alert('Error adding collaborator. Please try again.');
    }
  }

  async function removeCollaborator(collaboratorId) {
    if (!window.confirm('Remove this collaborator?')) return;

    try {
      const { error } = await supabase
        .from('collaborators')
        .delete()
        .eq('id', collaboratorId);

      if (error) throw error;

      alert('Collaborator removed!');
      loadCollaborators(selectedBook.id);
    } catch (error) {
      console.error('Error removing collaborator:', error);
      alert('Error: ' + error.message);
    }
  }

  function getFilteredAndSortedContacts() {
    let filtered = contacts.filter(contact => {
      const searchLower = searchTerm.toLowerCase();
      return (
        contact.first_name?.toLowerCase().includes(searchLower) ||
        contact.last_name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.city?.toLowerCase().includes(searchLower)
      );
    });

    switch (sortBy) {
      case 'alphabetical':
        return filtered.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
      case 'reverse':
        return filtered.sort((a, b) => (b.last_name || '').localeCompare(a.last_name || ''));
      case 'recent':
        return filtered.sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.submitted_at || 0) - new Date(b.submitted_at || 0));
      default:
        return filtered;
    }
  }

  function exportToCSV() {
    const contactsToExport = getFilteredAndSortedContacts();
    
    if (contactsToExport.length === 0) {
      alert('No contacts to export!');
      return;
    }

    const headers = [
      'First Name',
      'Last Name', 
      'Email',
      'Phone',
      'Street',
      'City',
      'State',
      'Zip',
      'Birthday',
      'Significant Other',
      'SO Phone',
      'SO Birthday',
      'Notes',
      'Tags',
      'Date Added'
    ];

    const rows = contactsToExport.map(contact => {
      const birthday = formatBirthdayForDisplay(contact.birthday) || '';
      const soBirthday = formatBirthdayForDisplay(contact.so_birthday) || '';
      
      return [
        contact.first_name || '',
        contact.last_name || '',
        contact.email || '',
        contact.phone || '',
        contact.street || contact.address_line1 || '',
        contact.city || '',
        contact.state || '',
        contact.zip_code || contact.zip || '',
        birthday,
        contact.so_name || contact.significant_other || '',
        contact.so_phone || '',
        soBirthday,
        contact.notes || '',
        (contact.tags || []).join('; '),
        contact.submitted_at ? new Date(contact.submitted_at).toLocaleDateString() : ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function toggleTag(tag) {
    const currentTags = showEditContact ? editingContact.tags : newContact.tags;
    const updatedTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    if (showEditContact) {
      setEditingContact({...editingContact, tags: updatedTags});
    } else {
      setNewContact({...newContact, tags: updatedTags});
    }
  }

  const displayContacts = getFilteredAndSortedContacts();
  const isOwner = selectedBook?.owner_id === user.id;
  const publicUrl = selectedBook ? `${window.location.origin}/${selectedBook.slug}` : '';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ fontSize: '18px', color: '#3b82f6', fontWeight: '500' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ width: '280px', background: '#f8fafc', padding: '20px', borderRight: '1px solid #e2e8f0' }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '15px' }}>📋 My Lists</h2>
          
          <button
            onClick={() => setShowCreateList(true)}
            style={{ width: '100%', padding: '12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', marginBottom: '15px' }}
          >
            + Create New List
          </button>

          <div
            onClick={() => handleSelectList(null)}
            style={{ padding: '12px', background: selectedList === null ? '#dbeafe' : 'white', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', border: selectedList === null ? '2px solid #3b82f6' : '2px solid transparent' }}
          >
            <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>📇 All Contacts</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>{contacts.length} contacts</div>
          </div>

          {lists.map(list => (
            <div
              key={list.id}
              onClick={() => handleSelectList(list)}
              style={{ padding: '12px', background: selectedList?.id === list.id ? '#dbeafe' : 'white', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', border: selectedList?.id === list.id ? '2px solid #3b82f6' : '2px solid transparent' }}
            >
              <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>{list.name}</div>
              {list.description && <div style={{ fontSize: '12px', color: '#64748b' }}>{list.description}</div>}
            </div>
          ))}
        </div>

        {upcomingBirthdays.length > 0 && (
          <div style={{ marginBottom: '30px', background: '#fef3c7', padding: '15px', borderRadius: '8px', border: '2px solid #fbbf24' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '10px' }}>
              🎂 Upcoming Birthdays
            </div>
            {upcomingBirthdays.map(contact => (
              <div key={contact.id} style={{ fontSize: '12px', color: '#78350f', marginBottom: '6px' }}>
                {contact.first_name} {contact.last_name}<br/>
                <span style={{ fontSize: '11px' }}>{formatBirthdayForDisplay(contact.birthday)}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => supabase.auth.signOut()}
          style={{ width: '100%', padding: '10px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#475569' }}
        >
          Sign Out
        </button>
      </div>

      <div style={{ flex: 1, padding: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e40af', margin: 0 }}>
                {selectedList ? selectedList.name : '📇 All Contacts'}
              </h1>
              <button
                onClick={() => setShowAddContact(true)}
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}
              >
                + Add Contact
              </button>
              <button
                onClick={exportToCSV}
                style={{ padding: '10px 20px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}
              >
                📥 Export CSV
              </button>
              <button
                onClick={() => setShowImportCSV(true)}
                style={{ padding: '10px 20px', background: '#ec4899', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}
              >
                📤 Import CSV
              </button>
              <button
                onClick={() => {
                  setBulkMode(!bulkMode);
                  setSelectedContactIds([]);
                }}
                style={{ padding: '10px 20px', background: bulkMode ? '#f59e0b' : '#64748b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}
              >
                {bulkMode ? '✓ Bulk Mode' : '☐ Bulk Select'}
              </button>
              {selectedList === null && displayContacts.length > 0 && !bulkMode && (
                <button
                  onClick={() => setShowAddToList(true)}
                  style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}
                >
                  📋 Add to List
                </button>
              )}
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                {displayContacts.length} {displayContacts.length === 1 ? 'contact' : 'contacts'}
                {!isOwner && <span style={{ marginLeft: '10px', color: '#10b981', fontWeight: '500' }}>• Shared with you</span>}
              </p>
            </div>
          </div>

          {bulkMode && (
            <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                {selectedContactIds.length} selected
              </span>
              <button
                onClick={selectAllContacts}
                style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Select All
              </button>
              <button
                onClick={deselectAllContacts}
                style={{ padding: '8px 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Deselect All
              </button>
              {selectedContactIds.length > 0 && (
                <>
                  <button
                    onClick={bulkDeleteContacts}
                    style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                  >
                    🗑️ Delete Selected
                  </button>
                  {lists.length > 0 && (
                    <div style={{ position: 'relative' }}>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            bulkAddToList(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                      >
                        <option value="">Add to List...</option>
                        {lists.map(list => (
                          <option key={list.id} value={list.id}>{list.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {showCreateList && (
            <CreateList
              onClose={() => setShowCreateList(false)}
              onListCreated={(newList) => {
                loadLists();
                handleSelectList(newList);
              }}
            />
          )}

          {showImportCSV && (
            <ImportCSV
              onClose={() => setShowImportCSV(false)}
              onImportComplete={() => {
                if (selectedList) {
                  loadContactsForList(selectedList.id);
                } else {
                  loadContacts(selectedBook.id);
                }
              }}
              addressBookId={selectedBook.id}
            />
          )}

          {showDuplicateWarning && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px' }}>
              <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '100%' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#f59e0b', marginBottom: '20px' }}>
                  ⚠️ Possible Duplicate Detected
                </h2>
                
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                  Found {duplicateContacts.length} existing contact{duplicateContacts.length > 1 ? 's' : ''} with similar name:
                </p>

                {duplicateContacts.map(contact => (
                  <div key={contact.id} style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                      {contact.first_name} {contact.last_name}
                    </div>
                    {contact.email && <div style={{ fontSize: '13px', color: '#64748b' }}>📧 {contact.email}</div>}
                    {contact.phone && <div style={{ fontSize: '13px', color: '#64748b' }}>📞 {contact.phone}</div>}
                    {contact.city && <div style={{ fontSize: '13px', color: '#64748b' }}>📍 {contact.city}, {contact.state}</div>}
                    
                    <button
                      onClick={() => handleMergeDuplicate(contact)}
                      style={{ marginTop: '10px', padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                    >
                      Merge with This Contact
                    </button>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={handleAddAnyway}
                    style={{ flex: 1, padding: '12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Add Anyway
                  </button>
                  <button
                    onClick={() => {
                      setShowDuplicateWarning(false);
                      setPendingContact(null);
                    }}
                    style={{ flex: 1, padding: '12px', background: '#64748b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {showAddToList && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>Add Contacts to List</h2>
                
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>Select contacts:</div>
                  {displayContacts.map(contact => (
                    <label key={contact.id} style={{ display: 'flex', alignItems: 'center', padding: '10px', background: '#f8fafc', borderRadius: '6px', marginBottom: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedContactsForList.includes(contact.id)}
                        onChange={() => toggleContactSelection(contact.id)}
                        style={{ marginRight: '10px' }}
                      />
                      <span>{contact.first_name} {contact.last_name}</span>
                    </label>
                  ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>Choose a list:</div>
                  {lists.map(list => (
                    <button
                      key={list.id}
                      onClick={() => addContactsToList(list.id)}
                      style={{ width: '100%', padding: '12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '8px', textAlign: 'left', fontWeight: '500' }}
                    >
                      {list.name}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setShowAddToList(false);
                    setSelectedContactsForList([]);
                  }}
                  style={{ width: '100%', padding: '12px', background: '#64748b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(showAddContact || showEditContact) && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
              <div style={{ background: 'white', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
                  {showEditContact ? 'Edit Contact' : 'Add New Contact'}
                </h2>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="First Name *"
                    value={showEditContact ? editingContact.firstName : newContact.firstName}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, firstName: e.target.value})
                      : setNewContact({...newContact, firstName: e.target.value})}
                    style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="Last Name *"
                    value={showEditContact ? editingContact.lastName : newContact.lastName}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, lastName: e.target.value})
                      : setNewContact({...newContact, lastName: e.target.value})}
                    style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="email"
                    placeholder="Email"
                    value={showEditContact ? editingContact.email : newContact.email}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, email: e.target.value})
                      : setNewContact({...newContact, email: e.target.value})}
                    style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={showEditContact ? editingContact.phone : newContact.phone}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, phone: e.target.value})
                      : setNewContact({...newContact, phone: e.target.value})}
                    style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={showEditContact ? editingContact.street : newContact.street}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, street: e.target.value})
                      : setNewContact({...newContact, street: e.target.value})}
                    style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="City"
                    value={showEditContact ? editingContact.city : newContact.city}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, city: e.target.value})
                      : setNewContact({...newContact, city: e.target.value})}
                    style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={showEditContact ? editingContact.state : newContact.state}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, state: e.target.value})
                      : setNewContact({...newContact, state: e.target.value})}
                    style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="Zip"
                    value={showEditContact ? editingContact.zip : newContact.zip}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, zip: e.target.value})
                      : setNewContact({...newContact, zip: e.target.value})}
                    style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                    🎂 Birthday (Optional)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                    <select
                      value={showEditContact ? editingContact.birthMonth : newContact.birthMonth}
                      onChange={(e) => showEditContact 
                        ? setEditingContact({...editingContact, birthMonth: e.target.value})
                        : setNewContact({...newContact, birthMonth: e.target.value})}
                      style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white' }}
                    >
                      <option value="">Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      placeholder="Day"
                      value={showEditContact ? editingContact.birthDay : newContact.birthDay}
                      onChange={(e) => showEditContact 
                        ? setEditingContact({...editingContact, birthDay: e.target.value})
                        : setNewContact({...newContact, birthDay: e.target.value})}
                      style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={showEditContact ? editingContact.hasSignificantOther : newContact.hasSignificantOther}
                      onChange={(e) => showEditContact 
                        ? setEditingContact({...editingContact, hasSignificantOther: e.target.checked})
                        : setNewContact({...newContact, hasSignificantOther: e.target.checked})}
                      style={{ width: '18px', height: '18px' }}
                    />
                    💑 Add Significant Other
                  </label>
                </div>

                {(showEditContact ? editingContact.hasSignificantOther : newContact.hasSignificantOther) && (
                  <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <input
                        type="text"
                        placeholder="SO First Name"
                        value={showEditContact ? editingContact.soFirstName : newContact.soFirstName}
                        onChange={(e) => showEditContact 
                          ? setEditingContact({...editingContact, soFirstName: e.target.value})
                          : setNewContact({...newContact, soFirstName: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      />
                      <input
                        type="text"
                        placeholder="SO Last Name"
                        value={showEditContact ? editingContact.soLastName : newContact.soLastName}
                        onChange={(e) => showEditContact 
                          ? setEditingContact({...editingContact, soLastName: e.target.value})
                          : setNewContact({...newContact, soLastName: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <input
                        type="tel"
                        placeholder="SO Phone"
                        value={showEditContact ? editingContact.soPhone : newContact.soPhone}
                        onChange={(e) => showEditContact 
                          ? setEditingContact({...editingContact, soPhone: e.target.value})
                          : setNewContact({...newContact, soPhone: e.target.value})}
                        style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      />
                    </div>

                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                      SO Birthday (Optional)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                      <select
                        value={showEditContact ? editingContact.soBirthMonth : newContact.soBirthMonth}
                        onChange={(e) => showEditContact 
                          ? setEditingContact({...editingContact, soBirthMonth: e.target.value})
                          : setNewContact({...newContact, soBirthMonth: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white' }}
                      >
                        <option value="">Month</option>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        placeholder="Day"
                        value={showEditContact ? editingContact.soBirthDay : newContact.soBirthDay}
                        onChange={(e) => showEditContact 
                          ? setEditingContact({...editingContact, soBirthDay: e.target.value})
                          : setNewContact({...newContact, soBirthDay: e.target.value})}
                        style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                    📝 Notes (Optional)
                  </div>
                  <textarea
                    placeholder="Add any notes about this contact..."
                    value={showEditContact ? editingContact.notes : newContact.notes}
                    onChange={(e) => showEditContact 
                      ? setEditingContact({...editingContact, notes: e.target.value})
                      : setNewContact({...newContact, notes: e.target.value})}
                    rows={3}
                    style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                    🏷️ Tags (Optional)
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {availableTags.map(tag => {
                      const isSelected = (showEditContact ? editingContact.tags : newContact.tags).includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.id)}
                          style={{
                            padding: '6px 12px',
                            background: isSelected ? tag.color : 'white',
                            color: isSelected ? 'white' : tag.color,
                            border: `2px solid ${tag.color}`,
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{tag.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={showEditContact ? saveEditContact : addContact}
                    disabled={saving}
                    style={{ flex: 1, padding: '12px', background: saving ? '#94a3b8' : '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '500' }}
                  >
                    {saving ? 'Saving...' : showEditContact ? 'Save Changes' : 'Add Contact'}
                  </button>
                  <button
                    onClick={() => {
                      if (showEditContact) {
                        setShowEditContact(false);
                        setEditingContact(null);
                      } else {
                        setShowAddContact(false);
                        setNewContact({
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          street: '',
                          city: '',
                          state: '',
                          zip: '',
                          birthMonth: '',
                          birthDay: '',
                          hasSignificantOther: false,
                          soFirstName: '',
                          soLastName: '',
                          soPhone: '',
                          soBirthMonth: '',
                          soBirthDay: '',
                          notes: '',
                          tags: []
                        });
                      }
                    }}
                    style={{ flex: 1, padding: '12px', background: '#64748b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}
                  >
                    Cancel
                  </button>
                </div>

                {showEditContact && (
                  <>
                    {selectedList && (
                      <button
                        onClick={() => removeContactFromList(editingContact.id, `${editingContact.firstName} ${editingContact.lastName}`)}
                        style={{ width: '100%', marginTop: '15px', padding: '12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}
                      >
                        📋 Remove from "{selectedList.name}"
                      </button>
                    )}
                    <button
                      onClick={() => deleteContact(editingContact.id, `${editingContact.firstName} ${editingContact.lastName}`)}
                      style={{ width: '100%', marginTop: '10px', padding: '12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}
                    >
                      🗑️ Delete Contact Permanently
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {selectedBook && (
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                  👥 Collaborators
                </h3>
                {isOwner && (
                  <button
                    onClick={() => setShowInvite(!showInvite)}
                    style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                  >
                    + Invite
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: '500', color: '#1e293b' }}>
                    {user.email} {isOwner && '(You)'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>Owner</div>
                </div>
              </div>

              {collaborators.filter(collab => collab.user_id !== selectedBook.owner_id).map(collab => (
                <div key={collab.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontWeight: '500', color: '#1e293b' }}>
                      {collab.users?.email || 'Unknown'}
                      {collab.user_id === user.id && ' (You)'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', textTransform: 'capitalize' }}>
                      {collab.role}
                    </div>
                  </div>
                  {isOwner && (
                    <button
                      onClick={() => removeCollaborator(collab.id)}
                      style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}

              {showInvite && (
                <div style={{ marginTop: '15px', padding: '15px', background: '#f0f9ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px', color: '#1e293b' }}>
                    Invite by email:
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="email"
                      placeholder="colleague@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      style={{ flex: 1, padding: '10px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      onKeyPress={(e) => e.key === 'Enter' && inviteCollaborator()}
                    />
                    <button
                      onClick={inviteCollaborator}
                      style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                    >
                      Send Invite
                    </button>
                    <button
                      onClick={() => {
                        setShowInvite(false);
                        setInviteEmail('');
                      }}
                      style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                    Note: They must create an account first before you can invite them.
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedBook && (
            <div style={{ background: '#dbeafe', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                📤 Share this link to collect addresses:
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={publicUrl}
                  readOnly
                  style={{ flex: 1, padding: '10px', border: '2px solid #3b82f6', borderRadius: '8px', fontSize: '14px', background: 'white' }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    alert('Link copied!');
                  }}
                  style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}
                >
                  📋 Copy
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍 Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: '1', minWidth: '200px', padding: '12px 16px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '8px', outline: 'none' }}
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ padding: '12px 16px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: 'white' }}
            >
              <option value="alphabetical">A-Z (Last Name)</option>
              <option value="reverse">Z-A (Last Name)</option>
              <option value="recent">Recently Added</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>

          {displayContacts.length === 0 ? (
            <div style={{ background: 'white', padding: '60px 20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📭</div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>
                {searchTerm ? 'No contacts found' : selectedList ? 'No contacts in this list' : 'No contacts yet'}
              </h3>
              <p style={{ color: '#64748b', fontSize: '15px' }}>
                {searchTerm ? 'Try a different search term' : selectedList ? 'Add contacts to this list from "All Contacts"' : 'Add a contact manually or share your link above to start collecting addresses!'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {displayContacts.map((contact) => {
                const displayBirthday = formatBirthdayForDisplay(contact.birthday);
                const displaySoBirthday = formatBirthdayForDisplay(contact.so_birthday);
                const isSelected = selectedContactIds.includes(contact.id);
                
                return (
                  <div
                    key={contact.id}
                    style={{ 
                      background: 'white', 
                      padding: '24px', 
                      borderRadius: '12px', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
                      position: 'relative',
                      border: isSelected ? '3px solid #3b82f6' : 'none'
                    }}
                  >
                    {bulkMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBulkSelection(contact.id)}
                        style={{ position: 'absolute', top: '10px', left: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                    )}
                    <button
                      onClick={() => openEditContact(contact)}
                      style={{ position: 'absolute', top: '10px', right: '10px', padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                    >
                      ✏️ Edit
                    </button>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', paddingRight: '60px', paddingLeft: bulkMode ? '30px' : '0' }}>
                        👤 {contact.first_name} {contact.last_name}
                      </h3>
                      {contact.tags && contact.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {contact.tags.map(tagId => {
                            const tag = availableTags.find(t => t.id === tagId);
                            return tag ? (
                              <span
                                key={tagId}
                                style={{
                                  padding: '4px 8px',
                                  background: tag.color,
                                  color: 'white',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}
                              >
                                {tag.label}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>

                    {contact.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px' }}>
                        <span>📧</span>
                        <span style={{ wordBreak: 'break-word' }}>{contact.email}</span>
                      </div>
                    )}

                    {contact.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px' }}>
                        <span>📞</span>
                        <span>{contact.phone}</span>
                      </div>
                    )}

                    {displayBirthday && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px' }}>
                        <span>🎂</span>
                        <span>{displayBirthday}</span>
                      </div>
                    )}

                    {(contact.so_name || contact.significant_other) && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px' }}>
                          <span>💑</span>
                          <span>{contact.so_name || contact.significant_other}</span>
                        </div>
                        {contact.so_phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px', marginLeft: '24px' }}>
                            <span>📞</span>
                            <span>{contact.so_phone}</span>
                          </div>
                        )}
                        {displaySoBirthday && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#64748b', fontSize: '14px', marginLeft: '24px' }}>
                            <span>🎂</span>
                            <span>{displaySoBirthday}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {(contact.street || contact.address_line1 || contact.city) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#64748b', fontSize: '14px', marginTop: '12px' }}>
                        <span>🏠</span>
                        <div>
                          {(contact.street || contact.address_line1) && <div>{contact.street || contact.address_line1}</div>}
                          {(contact.city || contact.state || contact.zip || contact.zip_code) && (
                            <div>
                              {contact.city}{contact.city && contact.state && ', '}{contact.state}{(contact.zip || contact.zip_code) && ` ${contact.zip || contact.zip_code}`}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {contact.notes && (
                      <div style={{ marginTop: '12px', padding: '10px', background: '#fffbeb', borderRadius: '6px', borderLeft: '3px solid #fbbf24' }}>
                        <div style={{ fontSize: '12px', color: '#92400e', fontStyle: 'italic', lineHeight: '1.5' }}>
                          {contact.notes.length > 100 ? (
                            <>
                              "{contact.notes.substring(0, 100)}..."
                              <button
                                onClick={() => openEditContact(contact)}
                                style={{ 
                                  marginLeft: '5px',
                                  color: '#b45309', 
                                  background: 'none', 
                                  border: 'none', 
                                  textDecoration: 'underline', 
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  padding: 0
                                }}
                              >
                                Read more
                              </button>
                            </>
                          ) : (
                            `"${contact.notes}"`
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
